import classNames from "classnames";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import BootstrapDatePicker from "components/common/custom/forms/BootDatePicker";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import IconButton from "components/common/IconButton";
import MultiSelect from "components/common/MultiSelect";
import { ageCalculator, getToday } from "helpers/utils";
import { toGregorian } from "hijri-converter";
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Col, Form, FormControl, InputGroup, Row } from "react-bootstrap";
import { Send } from "react-bootstrap-icons";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  GetAllDropDownData,
  GetInsuranceList,
  SearchPatientByID,
} from "./config/eligibilityCheckApi";
import { toast } from "react-toastify";
import moment from "moment";
import { handleResetPatientSearch } from "./config/eligibilityCheckSlice";
import EligibilityCCHIModal from "./components/EligibilityCCHIModal";
import IdentifierInput from "./components/IdentifierInput";

const EligibilityCheckForm = ({
  control,
  watch,
  errors,
  isSubmitted,
  setValue,
  reset,
  handleReset,
}) => {
  const dispatch = useDispatch();
  const {
    insuranceList,
    genderList,
    maritalList,
    relationshipList,
    religionList,
    professionList,
    processPriorityList,
    nationalityList,
    purposeList,
    patientSearchData,
    eligibilityCheckData,
    identifierTypeList,
  } = useSelector((state) => state.eligibilityCheck);

  const [showEligibilityModal, setShowEligibilityModal] = useState(false);

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const { userData } = useSelector((state) => state.auth);
  const [isUpdating, setIsUpdating] = useState(false);
  const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];
  const hijriRef = useRef(null);
  // Add this useEffect to handle initial form setup
  useEffect(() => {
    // Reset form with empty values first
    reset();

    dispatch(handleResetPatientSearch());
    // Then fetch initial data
    dispatch(GetInsuranceList());
    dispatch(GetAllDropDownData());

    // Set up cleanup
    return () => {
      dispatch(handleResetPatientSearch());
    };
  }, []);

  useEffect(() => {
    const newBorn = watch("new_born");
    const relationship = relationshipList?.find((item) =>
      newBorn ? item.patrtl_code === "child" : item.patrtl_code === "self"
    );

    if (relationship) {
      setValue("relationship", relationship);
    }
  }, [watch("new_born"), relationshipList, setValue]);

  // Modify the purpose list useEffect
  useEffect(() => {
    if (purposeList?.length > 0) {
      // Create array of all purposes
      const allPurposes = purposeList.map((item) => ({
        value: item.eligbpurp_code,
        label: item.eligbpurp_name,
      }));

      // Update form value without triggering validation
      setValue("purpose", allPurposes, { shouldValidate: false });
    }
    setValue(
      "branch",
      allBranches?.find(
        (item) => item?.branch_cchino === selectedBranch?.branch_cchino
      )
    );
  }, [purposeList, setValue]);
  const populateForm = (data) => {
    if (!data) return;
    setValue("branch", selectedBranch)
    setValue("patient_mrn", data.patient_mrn);
    setValue("first_name", data.patient_firstname);
    setValue("family_name", data.patient_familyname);
    setValue("birth_date", data.patient_dob);
    setValue(
      "gender",
      genderList?.find((item) => item?.genders_code === data.genders_code)
    );
    setValue(
      "marital_status",
      maritalList?.find(
        (item) => item?.maritalstatus_code === data.maritalstatus_code
      )
    );
    setValue(
      "profession",
      professionList?.find((item) => item?.occup_code === data.occup_code)
    );

    setValue(
      "insurance",
      insuranceList?.find(
        (item) => item?.insurance_company_id === data.insurance_company_id
      )
    );
    setValue(
      "identifier_type",
      identifierTypeList?.find(
        (item) => item?.ident_type_id === data.ident_type_id
      )
    );
    setValue("member_id", data.patient_ins_memid);
    setValue("network", data.patient_ins_network);
    setValue("policy_holder", data.patpolcy_name);
    setValue("policy_no", data.patient_ins_polynum);
    setValue("class_name", data.patient_ins_clsnme);
    setValue("id", data.patient_nationalid);

    setValue(
      "relationship",
      watch("new_born")
        ? relationshipList?.find((item) => item?.patrtl_code === "child")
        : relationshipList?.find((item) => item?.patrtl_code === "self")
    );
    setValue(
      "process_priority",
      processPriorityList?.find((item) => item?.prcspri_code === "normal")
    );

    setValue("service_period_from_date", getToday());
    const allPurposes = purposeList.map((item) => ({
      value: item.eligbpurp_code,
      label: item.eligbpurp_name,
    }));
    setValue("purpose", allPurposes, { shouldValidate: false });
  };

  useEffect(() => {
    if (patientSearchData && !patientSearchData?.length) {
      reset();
      populateForm(patientSearchData);
    } else if (patientSearchData?.length > 0) {
      reset();

      setShowEligibilityModal(true);
    }
  }, [patientSearchData]);

  useEffect(() => {
    if (watch("new_born")) {
      setValue(
        "relationship",
        relationshipList?.find((item) => item?.patrtl_code === "child")
      );
    }
  }, [watch("new_born")]);



  // Handle Gregorian date changes
  const handleGregorianDate = (date) => {
    if (!date) {
      setValue("birth_date", null);
      setValue("hijri_date_birth", "");
      return;
    }

    setIsUpdating(true);
    try {
      const { ageInHijri } = ageCalculator(date);
      const hijriDateStr = `${String(ageInHijri.hijriYear).padStart(
        4,
        "0"
      )}-${String(ageInHijri.hijriMonth).padStart(2, "0")}-${String(
        ageInHijri.hijriDay
      ).padStart(2, "0")}`;

      setValue("birth_date", date);
      setValue("hijri_date_birth", hijriDateStr);

      if (hijriRef.current) {
        hijriRef.current.value = hijriDateStr.split("-").reverse().join("-");
      }
    } catch (error) {
      console.error("Date conversion error:", error);
    }
    setIsUpdating(false);
  };

  // Handle Hijri date changes
  const handleHijriDate = (e) => {
    if (isUpdating) return;
    const value = e.target.value.replace(/[^0-9-]/g, "");

    if (!value) {
      setValue("hijri_date_birth", "");
      setValue("birth_date", null);
      return;
    }

    setIsUpdating(true);
    try {
      const [day, month, year] = value.split("-").map(Number);
      const greg = toGregorian(year, month, day);
      const gregDate = new Date(greg.gy, greg.gm - 1, greg.gd);

      setValue("birth_date", gregDate);
      setValue(
        "hijri_date_birth",
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
          2,
          "0"
        )}`
      );
    } catch (error) {
      console.error("Invalid Hijri date:", error);
      setValue("birth_date", null);
    }
    setIsUpdating(false);
  };

  // Handle Hijri date picker selection
  const handleHijriPickerChange = (event) => {
    // if (!isUpdating) {
    const [hy, hm, hd] = event.detail.split("-");
    handleHijriDate({ target: { value: `${hd}-${hm}-${hy}` } });
    // }
  };

  return (
    <>
      <Row alignItems="center">
        {userData?.employee?.syspri_id === 1 && (
          <Col xs={12} sm={6} md={3} xl={2}>
            <label className="form-label">
              Branch <span className="text-danger">*</span>
            </label>
            <Controller
              name="branch"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  isMulti={false}
                  isClearable={true}
                  options={allBranches.map((b) => ({
                    value: b.branch_id,
                    label: b.branch_name,
                    cchi: b.branch_cchino,
                  }))}
                  value={
                    field?.value
                      ? {
                        value: field?.value?.branch_id,
                        label: field?.value?.branch_name,
                        cchi: field?.value?.branch_cchino,
                      }
                      : null
                  }
                  onChange={(selected) => {
                    setValue(
                      "branch",
                      selected
                        ? {
                          branch_id: selected?.value,
                          branch_name: selected?.label,
                          branch_cchino: selected?.cchi,
                        }
                        : null
                    );
                  }}
                  className={classNames({
                    "is-invalid": errors.branch,
                  })}
                  placeholder="Choose branches..."
                />
              )}
            />
          </Col>
        )}
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Insurance <span className="text-danger">*</span>
          </label>
          <Controller
            name="insurance"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                options={insuranceList?.map((payer) => ({
                  value: payer.insurance_company_id,
                  code: payer.insurance_direct,
                  label:
                    payer.insurance_direct === 1
                      ? payer.payer_name
                      : `${payer.payer_name} - ${payer.insurance_shname}`,
                  original: payer, // Keep full object if needed
                }))}
                isClearable={true}
                placeholder="Choose..."
                onChange={(selected) => {
                  setValue(
                    "insurance",
                    selected
                      ? {
                        payer_name: selected?.original?.payer_name,
                        insurance_company_id:
                          selected?.original.insurance_company_id,
                        insurance_direct: selected?.original.insurance_direct,
                        insurance_shname: selected?.original.insurance_shname,
                      }
                      : null
                  );
                }}
                value={
                  field.value
                    ? {
                      value: field.value.payer_id,
                      label:
                        field.value.insurance_direct === 1
                          ? field.value.payer_name
                          : `${field.value.payer_name} - ${field.value.insurance_shname}`,
                    }
                    : null
                }
              />
            )}
          />
          {errors?.insurance && isSubmitted && (
            <Form.Control.Feedback type="invalid" />
          )}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Identifier Type <span className="text-danger">*</span>
          </label>
          <Controller
            name="identifier_type"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                closeMenuOnSelect={false}
                isMulti={false}
                options={identifierTypeList?.map((item) => ({
                  value: item.ident_type_id,
                  label: item.ident_type_name,
                  code: item.ident_type_code
                }))}
                value={
                  field.value
                    ? {
                      value: field.value.ident_type_id,
                      label: field.value.ident_type_name,
                      code: field.value.ident_type_code
                    }
                    : null
                }
                onChange={(selected) => {
                  setValue(
                    "identifier_type",
                    selected
                      ? {
                        ident_type_id: selected.value,
                        ident_type_name: selected.label,
                        ident_type_code: selected.code
                      }
                      : null
                  );
                }}
                className={classNames({
                  "is-invalid":
                    errors.identifier_type ||
                    (isSubmitted && !watch("identifier_type")),
                })}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                placeholder="Choose ..."
              />
            )}
          />
          {errors?.identifier_type && <Form.Control.Feedback type="invalid" />}
        </Col>
        <IdentifierInput
          control={control}
          watch={watch}
          nationalityList={nationalityList}
          populateForm={populateForm}
          reset={reset}
        />
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="patient_mrn"
            size="sm"
            errors={errors}
            label="MRN"
            control={control}
            required={false}
            placeholder="MRN"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="first_name"
            errors={errors}
            size="sm"
            label="First Name"
            control={control}
            required={true}
            placeholder="First Name"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="family_name"
            errors={errors}
            size="sm"
            label="Family Name"
            control={control}
            required={true}
            placeholder="Family Name"
          />
        </Col>
        <Col xs={12} sm={4} md={3} xl={2} style={{ height: "3rem" }}>
          <Row>
            <Col xs={8}>
              {!watch("is_hijri") && (
                <BootDatePicker
                  name="birth_date"
                  label="Birth Date"
                  size="sm"
                  control={control}
                  rules={{ required: "Birth date is required" }}
                  errors={errors}
                  selected={watch("birth_date")}
                  onChange={handleGregorianDate}
                  placeholder="dd/mm/yyyy"
                  maxDate={new Date()}
                  isRequired={true}
                />
              )}
              {watch("is_hijri") && (
                <div className="hijri-datepicker-wrapper">
                  <Form.Group className="">
                    <Form.Label htmlFor="calender">
                      {"Hijri Date"} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      ref={hijriRef}
                      type="text"
                      id="calender"
                      size="sm"
                      onChange={handleHijriDate}
                      placeholder="dd-mm-yyyy"
                      value={
                        watch("hijri_date_birth")
                          ?.split("-")
                          .reverse()
                          .join("-") || ""
                      }
                    />
                  </Form.Group>
                  <datepicker-hijri
                    placeholder="dd-mm-yyyy"
                    reference="calender"
                    size="sm"
                    placement="auto"
                    date-format="iDD-iMM-iYYYY"
                    max-date={new Date()}
                    onSelectedDateChanged={handleHijriPickerChange}
                  ></datepicker-hijri>
                </div>
              )}
            </Col>
            <Col xs={4}>
              <BootCheckbox
                name="is_hijri"
                label="Is Hijri?"
                size="lg"
                control={control}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label mb-2">
            Gender <span className="text-danger">*</span>
          </label>
          <Controller
            name="gender"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                closeMenuOnSelect={false}
                isMulti={false}
                options={genderList?.map((item) => ({
                  value: item.genders_code,
                  label: item.genders_disply,
                }))}
                value={
                  field.value
                    ? {
                      value: field.value.genders_code,
                      label: field.value.genders_disply,
                    }
                    : null
                }
                onChange={(selected) => {
                  setValue(
                    "gender",
                    selected
                      ? {
                        genders_code: selected.value,
                        genders_disply: selected.label,
                      }
                      : null
                  );
                }}
                className={classNames({
                  "is-invalid":
                    errors.gender || (isSubmitted && !watch("gender")),
                })}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                placeholder="Choose ..."
              />
            )}
          />
          {errors?.gender && <Form.Control.Feedback type="invalid" />}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Marital Status <span className="text-danger">*</span>
          </label>
          <Controller
            name="marital_status"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                closeMenuOnSelect={false}
                isMulti={false}
                options={maritalList?.map((marital) => ({
                  value: marital?.maritalstatus_code,
                  label: marital?.maritalstatus_disply,
                }))}
                value={
                  field.value
                    ? {
                      value: field.value.maritalstatus_code,
                      label: field.value.maritalstatus_disply,
                    }
                    : null
                }
                onChange={(selected) => {
                  setValue(
                    "marital_status",
                    selected
                      ? {
                        maritalstatus_code: selected.value,
                        maritalstatus_disply: selected.label,
                      }
                      : null
                  );
                }}
                className={classNames({
                  "is-invalid":
                    errors.marital_status ||
                    (isSubmitted && !watch("marital_status")),
                })}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                placeholder="Choose ..."
              />
            )}
          />
          {errors?.marital_status && <Form.Control.Feedback type="invalid" />}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Profession <span className="text-danger">*</span>
          </label>
          <Controller
            name="profession"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                closeMenuOnSelect={false}
                isMulti={false}
                options={professionList?.map((profession) => ({
                  value: profession?.occup_code,
                  label: profession?.occup_disply,
                }))}
                value={
                  field.value
                    ? {
                      value: field.value.occup_code,
                      label: field.value.occup_disply,
                    }
                    : null
                }
                onChange={(selected) => {
                  setValue(
                    "profession",
                    selected
                      ? {
                        occup_code: selected?.value,
                        occup_disply: selected?.label,
                      }
                      : null
                  );
                }}
                className={classNames({
                  "is-invalid":
                    errors.profession || (isSubmitted && !watch("profession")),
                })}
                placeholder="Choose ..."
              />
            )}
          />
          {errors?.profession && <Form.Control.Feedback type="invalid" />}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="member_id"
            errors={errors}
            label="Member ID"
            control={control}
            required={true}
            rules={{ required: true }}
            size="sm"
            placeholder="Member Id"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="network"
            errors={errors}
            size="sm"
            label="Network"
            required={false}
            control={control}
            placeholder="Network"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="policy_no"
            errors={errors}
            label="Policy Number"
            required={true}
            rules={{ required: true }}
            size="sm"
            control={control}
            placeholder="Policy Number"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="policy_holder"
            errors={errors}
            label="Policy Holder"
            required={true}
            rules={{ required: true }}
            control={control}
            size="sm"
            placeholder="Policy Holder"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="class_name"
            errors={errors}
            label="Class Name"
            size="sm"
            control={control}
            required={true}
            rules={{ required: true }}
            placeholder="Class Name"
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <TextInput
            name="mobile_no"
            size="sm"
            label="Mobile No."
            control={control}
            placeholder="05xxxxxxxx"
            type="tel"
            inputMode="numeric"
            className={classNames({
              "is-invalid":
                errors.mobile_no ||
                (isSubmitted && watch("mobile_no")?.value === ""),
            })}
            required={false}
            rules={{
              required: false,
              validate: (value) => {
                if (!value) return true; // allow empty (not required)
                const isValidFormat = /^05\d{8}$/.test(value);
                if (!isValidFormat) {
                  return "Mobile number must start with 05 and be 10 digits long";
                }
                return true;
              },
            }}
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Relationship <span className="text-danger">*</span>
          </label>{" "}
          <Controller
            name="relationship"
            control={control}
            rules={{ required: true }}
            render={({ field, ref }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                ref={ref}
                closeMenuOnSelect={false}
                disabled={watch("new_born")}
                isMulti={false}
                options={relationshipList?.map((item) => ({
                  value: item.patrtl_code,
                  label: item?.patrtl_name,
                }))}
                value={
                  field?.value
                    ? {
                      value: field?.value?.patrtl_code,
                      label: field?.value?.patrtl_name,
                    }
                    : null
                }
                className={classNames({
                  "is-invalid":
                    errors.relationship ||
                    (isSubmitted && watch("relationship")?.value === ""),
                })}
                onChange={(selected) => {
                  setValue(
                    "relationship",
                    selected
                      ? {
                        patrtl_code: selected?.value,
                        patrtl_name: selected?.label,
                      }
                      : null
                  );
                }}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                placeholder="Choose ... "
              />
            )}
          />
          {errors?.relationship && <Form.Control.Feedback type="invalid" />}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">
            Process Priority <span className="text-danger">*</span>
          </label>
          <Controller
            name="process_priority"
            control={control}
            rules={{ required: true }}
            render={({ field, ref }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                ref={ref}
                closeMenuOnSelect={false}
                value={
                  field?.value
                    ? {
                      value: field?.value?.prcspri_code,
                      label: field?.value?.prcspri_name,
                    }
                    : null
                }
                isMulti={false}
                className={classNames({
                  "is-invalid":
                    errors.process_priority ||
                    (isSubmitted && watch("process_priority")?.value === ""),
                })}
                onChange={(selected) => {
                  setValue(
                    "process_priority",
                    selected
                      ? {
                        prcspri_code: selected?.value,
                        prcspri_name: selected?.label,
                      }
                      : null
                  );
                }}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                // {...field}
                options={processPriorityList?.map((item) => ({
                  value: item?.prcspri_code,
                  label: item?.prcspri_name,
                }))}
                placeholder="Choose ... "
              />
            )}
          />
          {errors?.process_priority && <Form.Control.Feedback type="invalid" />}
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <label className="form-label">Religion</label>
          <Controller
            name="religion"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                closeMenuOnSelect={false}
                isMulti={false}
                isClearable={true}
                options={religionList?.map((profession) => ({
                  value: profession?.religion_code,
                  label: profession?.religion_name,
                }))}
                value={
                  field.value
                    ? {
                      value: field.value.religion_code,
                      label: field.value.religion_name,
                    }
                    : null
                }
                onChange={(selected) => {
                  setValue(
                    "religion",
                    selected
                      ? {
                        religion_code: selected?.value,
                        religion_name: selected?.label,
                      }
                      : null
                  );
                }}
                placeholder="Choose ..."
              />
            )}
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <BootstrapDatePicker
            name="service_period_from_date"
            label="Service Period from"
            size="sm"
            control={control}
            rules={{ required: "Service Period from is required" }}
            errors={errors}
            selected={watch("service_period_from_date")}
            onChange={(date) => setValue("service_period_from_date", date)}
            placeholder="dd/mm/yyyy"
            minDate={new Date()}
            isRequired={true}
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <BootstrapDatePicker
            name="service_period_to_date"
            label="Service Period to"
            size="sm"
            control={control}
            rules={{ required: false }}
            errors={errors}
            selected={watch("service_period_to_date")}
            onChange={(date) => setValue("service_period_to_date", date)}
            placeholder="dd/mm/yyyy"
            minDate={new Date()}
            maxDate={watch("service_period_from_date")}
            isRequired={false}
          />
        </Col>
        <Col xs={12} sm={6} md={3} xl={2}>
          <Row>
            <Col xs={5} style={{ marginLeft: "8px" }}>
              <BootCheckbox
                label="Transfer"
                name="transfer"
                size="lg"
                control={control}
              />
            </Col>
            <Col xs={5}>
              <BootCheckbox
                label="Emergency"
                name="emergency"
                size="lg"
                control={control}
              />
            </Col>
          </Row>
        </Col>
        <Col xs={12} md={6}>
          <Row>
            <Col xs={9}>
              <label className="form-label">
                Purpose <span className="text-danger">*</span>
              </label>
              <Controller
                name="purpose"
                control={control}
                rules={{ required: true }}
                render={({ field, ref }) => (
                  <>
                    <MultiSelect
                      {...field}
                      menuPlacement="auto"
                      ref={ref}
                      closeMenuOnSelect={false}
                      value={watch("purpose")}
                      isMulti={true}
                      className={classNames({
                        "is-invalid":
                          errors.purpose ||
                          (isSubmitted && watch("purpose")?.length === 0),
                      })}
                      {...field}
                      options={purposeList?.map((item) => ({
                        value: item?.eligbpurp_code,
                        label: item?.eligbpurp_name,
                      }))}
                      onChange={(selected) => setValue("purpose", selected)}
                      placeholder="Choose ... "
                    />
                  </>
                )}
              />
              {errors?.purpose && <Form.Control.Feedback type="invalid" />}
            </Col>
            <Col xs={3}>
              <BootCheckbox
                label="New Born?"
                name="new_born"
                size="lg"
                control={control}
              />
            </Col>
          </Row>
        </Col>
        {watch("new_born") && (
          <motion.div
            style={{ border: "2px solid #d9f8eb" }}
            className="rounded p-3 position-relative  mt-3"
            initial={{ opacity: 0, y: 50 }} // Starting from down (y: 50px)
            animate={{
              opacity:
                watch("new_born") ||
                  watch("relationship")?.patrtl_code !== "self"
                  ? 1
                  : 0, // Show if new_born is true, else hide
              y:
                watch("new_born") ||
                  watch("relationship")?.patrtl_code !== "self"
                  ? 0
                  : 50, // Move to original position if new_born is true
            }}
            exit={{ opacity: 0, y: 50 }} // When exiting, move back down
            transition={{ duration: 0.5 }} // Duration of animation
          >
            {/* Floating Badge Header */}
            <div className="position-absolute top-0 start-50 translate-middle-y px-2">
              <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
                <span className="fs-11">
                  {watch("new_born") ? "New Born Info" : "Beneficiary Info"}
                </span>
              </span>
            </div>

            {/* Form Row */}
            <Row className="align-items-end g-3 pt-3">
              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="child_mrn"
                  label="MRN"
                  control={control}
                  size="sm"
                  required={watch("new_born")}
                  placeholder="MRN"
                  errors={errors}
                />
              </Col>

              <Col xs={12} sm={6} md={3} xl={2}>
                <label className="form-label">
                  Gender <span className="text-danger">*</span>
                </label>
                <Controller
                  name="child_gender"
                  control={control}
                  rules={{
                    required: watch("new_born"),
                  }}
                  render={({ field, ref }) => (
                    <MultiSelect
                      {...field}
                      ref={ref}
                      menuPlacement="auto"
                      closeMenuOnSelect={false}
                      isMulti={false}
                      className={classNames({
                        "is-invalid":
                          errors.child_gender ||
                          (isSubmitted && watch("child_gender")?.value === ""),
                      })}
                      options={[
                        { value: "", label: "Choose", selected: true },
                        { value: "male", label: "Male" },
                        { value: "female", label: "Female" },
                        { value: "other", label: "Other" },
                        { value: "unknown", label: "Unknown" },
                        {
                          value: "undifferentiated",
                          label: "Undifferentiated",
                        },
                        {
                          value: "sex_changed_to_male",
                          label: "Sex Changed to Male",
                        },
                        {
                          value: "sex_changed_to_female",
                          label: "Sex Changed to Female",
                        },
                        { value: "not_completed", label: "Not Completed" },
                      ]}
                      placeholder="Choose ..."
                    />
                  )}
                />
                {errors?.child_gender && (
                  <Form.Control.Feedback type="invalid" />
                )}
              </Col>

              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="child_first_name"
                  label="First Name"
                  control={control}
                  size="sm"
                  required={watch("new_born")}
                  placeholder="First Name"
                  errors={errors}
                />
              </Col>

              <Col xs={12} sm={6} md={3} xl={2}>
                <TextInput
                  name="child_family_name"
                  label="Family Name"
                  control={control}
                  size="sm"
                  required={watch("new_born")}
                  placeholder="Family Name"
                  errors={errors}
                />
              </Col>

              <Col xs={12} sm={6} md={3} xl={2}>
                <BootDatePicker
                  name="child_birth_date"
                  label="Birth Date"
                  control={control}
                  rules={{
                    required: watch("new_born"),
                  }}
                  errors={errors}
                  isRequired={watch("new_born")}
                  placeholder="dd/mm/yyyy"
                  selected={watch("child_birth_date")}
                  maxDate={new Date()}
                />
              </Col>
            </Row>
          </motion.div>
        )}
      </Row>
      {showEligibilityModal && (
        <EligibilityCCHIModal
          show={showEligibilityModal}
          handleClose={() => setShowEligibilityModal(false)}
          setValue={setValue}
          watch={watch}
        />
      )}
    </>
  );
};

export default EligibilityCheckForm;
